// Definición estática del guion del tour de bienvenida.
// El contenido (español) vive aquí; el comportamiento en tiempo real (before-hooks
// de móvil, navegación) lo inyecta WelcomeTour.tsx al mapear a los Step de Joyride.

export interface TourStepDef {
  /** Selector del target. 'body' = paso centrado (modal sin ancla). */
  readonly target: string;
  /**
   * Posición del tooltip. Siempre explícito (nunca se omite): dejarlo implícito
   * hace que react-joyride 3.1.0 reciba `placement` indefinido en su cálculo interno
   * de floating-ui justo tras una navegación reciente, y crashea en
   * `placement.startsWith(...)` (bug verificado en `node_modules/react-joyride/dist/index.mjs`).
   */
  readonly placement: 'center' | 'left' | 'right' | 'bottom';
  readonly titulo?: string;
  readonly contenido: string;
  /**
   * informativo = avanza con botón "Continuar"; accionable = avanza al navegar;
   * llegada = checkpoint tras un paso accionable — pausa (con "Continuar") para que
   * el usuario vea la pantalla que acaba de abrir antes de pedirle la siguiente acción.
   * Target = contenido principal (spotlight): solo esa área queda clara e interactiva;
   * header y sidebar quedan oscurecidos y bloqueados (evita que el usuario se desvíe
   * del orden del tour tocando el menú). TourTooltip.tsx reposiciona el tooltip fuera
   * del centro de la pantalla para no taparla.
   */
  readonly tipo: 'informativo' | 'accionable' | 'llegada';
  /** Solo en pasos accionables: ruta a la que debe llegar el usuario para avanzar. */
  readonly rutaEsperada?: string;
  /** Dónde vive el objetivo de un paso accionable. */
  readonly objetivo?: 'menu' | 'contenido';
  /** Texto del botón primario (informativos/llegada). */
  readonly primaryLabel?: string;
  /** Texto del botón de salida. */
  readonly skipLabel: string;
}

/**
 * Checkpoint de llegada tras un paso accionable: título = sección, descripción breve
 * de qué se gestiona ahí. Target = contenido principal (ver TourStepDef.tipo).
 */
function pasoLlegada(seccion: string, descripcion: string): TourStepDef {
  return {
    target: '[data-tour="contenido-principal"]',
    placement: 'bottom',
    titulo: seccion,
    contenido: descripcion,
    tipo: 'llegada',
    primaryLabel: 'Continuar',
    skipLabel: 'Salir',
  };
}

/**
 * Paso accionable: pide tocar el ítem `id` del menú lateral (`data-tour="menu-<id>"`)
 * y avanza cuando el usuario navega a `/lumisial/<id>`.
 */
function pasoAccionable(id: string, titulo: string, contenido: string): TourStepDef {
  return {
    target: `[data-tour="menu-${id}"]`,
    placement: 'right',
    titulo,
    contenido,
    tipo: 'accionable',
    objetivo: 'menu',
    rutaEsperada: `/lumisial/${id}`,
    skipLabel: 'Salir',
  };
}

/**
 * Paso accionable de regreso: pide tocar "Volver" para subir a Balance.
 *
 * Existe porque el recorrido promete "tú das los clics; cada paso indica dónde tocar", y este
 * tramo era el único que lo incumplía: al avanzar desde el checkpoint de Aportes navegaba solo
 * de vuelta a Balance, así que el usuario veía aparecer el paso de Movimientos sin entender cómo
 * había llegado ahí. Reportado en el testing manual del PR #237 — "se salta de la pantalla de
 * aportes a la de movimientos sin dejarle claro al usuario cómo pasó eso".
 *
 * Va en las dos vistas hijas, no solo en Aportes: el salto invisible era idéntico en Movimientos,
 * y dejar una con paso explícito y la otra sin él sería una inconsistencia nueva.
 *
 * El texto no nombra la pantalla de origen: el usuario acaba de leer el checkpoint que la nombra
 * y está parado en ella, así que decírselo otra vez solo abulta el paso.
 */
function pasoVolver(): TourStepDef {
  return {
    target: '[data-tour="volver-a-balance"]',
    placement: 'bottom',
    titulo: 'Volver a Balance',
    contenido: 'Toca "Volver" para regresar al Balance.',
    tipo: 'accionable',
    objetivo: 'contenido',
    rutaEsperada: '/lumisial/balance',
    skipLabel: 'Salir',
  };
}

// Orden = orden físico del menú (arriba→abajo). Balance está segundo, así que se visita temprano
// junto con sus vistas hijas, y el recorrido cierra en Campañas.
export const WELCOME_TOUR_STEPS: readonly TourStepDef[] = [
  {
    target: 'body',
    placement: 'center',
    titulo: '¿Quieres ver la app en 1 minuto?',
    contenido: 'Un recorrido rápido por las secciones principales. Tú das los clics; cada paso indica dónde tocar.',
    tipo: 'informativo',
    primaryLabel: 'Empezar',
    skipLabel: 'Ahora no',
  },
  pasoLlegada('Tu inicio', 'Aquí ves tu panel principal. Desde el menú de la izquierda llegas a cada sección.'),

  pasoAccionable('balance', 'Balance', 'Toca "Balance": es el centro de la administración.'),
  pasoLlegada('Balance', 'Aquí ves el balance consolidado. Los aportes y los movimientos que lo componen viven dentro de esta pantalla.'),

  { target: '[data-tour="acceso-aportes"]', placement: 'bottom', titulo: 'Aportes',
    contenido: 'Toca "Aportes" para continuar.',
    tipo: 'accionable', objetivo: 'contenido',
    rutaEsperada: '/lumisial/balance/aportes', skipLabel: 'Salir' },
  // Sin `rutaAlAvanzar`: el regreso a Balance dejó de ser automático y lo hace el usuario en el
  // paso siguiente, que es lo que vuelve entendible la llegada a Movimientos.
  pasoLlegada('Aportes', 'Aquí gestionas los aportes: registro, revisión y seguimiento.'),
  pasoVolver(),

  { target: '[data-tour="acceso-movimientos"]', placement: 'bottom', titulo: 'Movimientos',
    contenido: 'Toca "Movimientos" para continuar.',
    tipo: 'accionable', objetivo: 'contenido',
    rutaEsperada: '/lumisial/balance/movimientos', skipLabel: 'Salir' },
  pasoLlegada('Movimientos', 'Aquí gestionas los movimientos: el detalle de cada entrada y salida.'),
  pasoVolver(),

  pasoAccionable('miembros', 'Miembros', 'Toca "Miembros" para ver el registro de personas.'),
  pasoLlegada('Miembros', 'Aquí gestionas los miembros: registro, traslados e historial.'),
  pasoAccionable('actividades', 'Actividades', 'Toca "Actividades" para ver los eventos y sus ventas.'),
  pasoLlegada('Actividades', 'Aquí gestionas las actividades: productos, ventas y balance del evento.'),
  pasoAccionable('campanas', 'Campañas', 'Toca "Campañas" para ver las campañas de aportes.'),
  pasoLlegada('Campañas', 'Aquí gestionas las campañas: su registro y los aportes que recibe.'),
  {
    target: '[data-tour="help-menu"]',
    placement: 'left',
    titulo: '¡Listo!',
    contenido: 'Cuando quieras repasar el tour inicial, está siempre aquí, en el ícono de ayuda.',
    tipo: 'informativo',
    primaryLabel: 'Finalizar',
    skipLabel: 'Cerrar',
  },
] as const;
